import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Button from '../../../lib/button/Button';
import DropdownButton from '../../../lib/dropdown/DropdownButton';
import HelpDialogHomeContent from './HelpDilalogHomeContent';
import { envConfig } from '../../../config';
import { EditorService } from '../../../services/editor';

import VisualProgrammingIcon from '../../../assets/visual-programming-icon.svg';
import PublishProjectRichIcon from '../../../assets/publish-project-rich-icon.svg';
import TestingLogicIcon from '../../../assets/testing-logic-icon.svg';
import RelationalDatabaseIcon from '../../../assets/relational-database.png';
import DatabaseTableIcon from '../../../assets/database-table.png';
import HTTPEndpointIcon from '../../../assets/http-endpoint.svg';
import ContainerComputeIcon from '../../../assets/container-compute.svg';
import SharedDriveIcon from '../../../assets/shared-drive.svg';
import CloudFileSystemIcon from '../../../assets/cloud-file-system.svg';
import TerminalIcon from '../../../assets/terminal.svg';
import CronJobIcon from '../../../assets/cron-job.png';
import CloudAssetsIcon from '../../../assets/resources.svg';

const HelpDialog: React.FC<{ onClose: () => void; project: EditorService }> = ({
	onClose,
	project
}) => {
	const { tab } = useParams<{ id: string; tab: string }>();
	const navigate = useNavigate();

	const handleNavigation = (path: string) => {
		navigate(path);
	};


	const helpOptions = [
		{
			value: '',
			label: 'Home',
			description: 'Overview and main topics.',
			keywords: [{ word: 'index', score: 1 }, { word: 'start', score: 1 }, { word: 'main', score: 1 }],
			iconEnd: 'fa-solid fa-question-circle',
			onClick: () => handleNavigation(`${project?.editorBasePath}/help`)
		},
		{
			value: 'canvas-basics',
			label: 'Canvas basics',
			description: 'Learn how to navigate and use the logic editor canvas.',
			keywords: [{ word: 'editor', score: 1 }, { word: 'nodes', score: 1 }, { word: 'connections', score: 1 }, { word: 'move', score: 1 }],
			iconFile: VisualProgrammingIcon,
			iconEnd: 'fa-solid fa-pencil',
			onClick: () => handleNavigation(`${project?.editorBasePath}/help/canvas-basics`)
		},
		{
			value: 'datatypes',
			label: 'Data types',
			description: 'Understanding strings, numbers, booleans, arrays and objects.',
			keywords: [{ word: 'string', score: 1 }, { word: 'number', score: 1 }, { word: 'boolean', score: 1 }, { word: 'array', score: 1 }, { word: 'object', score: 1 }],
			iconEnd: 'fa-solid fa-keyboard',
			onClick: () => handleNavigation(`${project?.editorBasePath}/help/datatypes`)
		},
		{
			value: 'variables',
			label: 'Variables',
			description: 'Store data temporarily or permanently using variables.',
			keywords: [{ word: 'state', score: 1 }, { word: 'store', score: 1 }, { word: 'memory', score: 1 }, { word: 'save', score: 1 }],
			iconEnd: 'fa-solid fa-t',
			onClick: () => handleNavigation(`${project?.editorBasePath}/help/variables`)
		},
		{
			value: 'testing-logic',
			label: 'Testing logic',
			description: 'How to run tests and debug your application logic.',
			keywords: [{ word: 'test', score: 1 }, { word: 'debug', score: 1 }, { word: 'run', score: 1 }, { word: 'execute', score: 1 }],
			iconFile: TestingLogicIcon,
			iconEnd: 'fa-solid fa-check',
			onClick: () => handleNavigation(`${project?.editorBasePath}/help/testing-logic`)
		},
		{
			value: 'test-values',
			label: 'Test values',
			description: 'Provide mock data for testing your logic branches.',
			keywords: [{ word: 'mock', score: 1 }, { word: 'fake', score: 1 }, { word: 'input', score: 1 }, { word: 'stub', score: 1 }],
			iconEnd: 'fa-solid fa-circle-check',
			onClick: () => handleNavigation(`${project?.editorBasePath}/help/test-values`)
		},
		{
			value: 'operations',
			label: 'Operations',
			description: 'Perform mathematical, logical, or string operations.',
			keywords: [{ word: 'math', score: 1 }, { word: 'add', score: 1 }, { word: 'subtract', score: 1 }, { word: 'concat', score: 1 }],
			iconEnd: 'fa-solid fa-plus',
			onClick: () => handleNavigation(`${project?.editorBasePath}/help/operations`)
		},
		{
			value: 'scopes',
			label: 'Scopes',
			description: 'Understand how data is accessible within different logic boundaries.',
			keywords: [{ word: 'global', score: 1 }, { word: 'local', score: 1 }, { word: 'access', score: 1 }, { word: 'context', score: 1 }],
			iconEnd: 'fa-solid fa-layer-group',
			onClick: () => handleNavigation(`${project?.editorBasePath}/help/scopes`)
		},
		{
			value: 'loops',
			label: 'Loops',
			description: 'Iterate over lists or repeat actions multiple times.',
			keywords: [{ word: 'iterate', score: 1 }, { word: 'for', score: 1 }, { word: 'while', score: 1 }, { word: 'each', score: 1 }, { word: 'repeat', score: 1 }],
			iconEnd: 'fa-solid fa-rotate-right',
			onClick: () => handleNavigation(`${project?.editorBasePath}/help/loops`)
		},
		{
			value: 'search-node',
			label: 'Search node',
			description: 'Query and filter data efficiently.',
			keywords: [{ word: 'find', score: 1 }, { word: 'query', score: 1 }, { word: 'filter', score: 1 }, { word: 'look', score: 1 }],
			iconEnd: 'fa-solid fa-magnifying-glass',
			onClick: () => handleNavigation(`${project?.editorBasePath}/help/search-node`)
		},
		{
			value: 'custom-triggers',
			label: 'Custom triggers',
			description: 'Create events to trigger specific logic flows.',
			keywords: [{ word: 'event', score: 1 }, { word: 'trigger', score: 1 }, { word: 'fire', score: 1 }, { word: 'emit', score: 1 }],
			iconEnd: 'fa-solid fa-bolt',
			onClick: () => handleNavigation(`${project?.editorBasePath}/help/custom-triggers`)
		},
		{
			value: 'project-events',
			label: 'Project events',
			description: 'Listen to global events within your Elyx project.',
			keywords: [{ word: 'listen', score: 1 }, { word: 'global', score: 1 }, { word: 'webhook', score: 1 }, { word: 'on', score: 1 }],
			iconEnd: 'fa-solid fa-calendar-check',
			onClick: () => handleNavigation(`${project?.editorBasePath}/help/project-events`)
		},
		{
			value: 'scheduled-executions',
			label: 'Scheduled executions',
			description: 'Run logic automatically at specific times or intervals.',
			keywords: [{ word: 'cron', score: 1 }, { word: 'time', score: 1 }, { word: 'interval', score: 1 }, { word: 'automatic', score: 1 }],
			iconFile: CronJobIcon,
			iconEnd: 'fa-solid fa-clock',
			onClick: () => handleNavigation(`${project?.editorBasePath}/help/scheduled-executions`)
		},
		{
			value: 'definitions',
			label: 'Definitions',
			description: 'Create reusable logic blocks and custom models.',
			keywords: [{ word: 'reusable', score: 1 }, { word: 'custom', score: 1 }, { word: 'function', score: 1 }, { word: 'macro', score: 1 }],
			iconEnd: 'fa-solid fa-book',
			onClick: () => handleNavigation(`${project?.editorBasePath}/help/definitions`)
		},
		{
			value: 'costs-calculation',
			label: 'Costs calculation',
			description: 'Understand how usage and computing costs are measured.',
			keywords: [{ word: 'billing', score: 1 }, { word: 'price', score: 1 }, { word: 'money', score: 1 }, { word: 'usage', score: 1 }],
			iconEnd: 'fa-solid fa-dollar-sign',
			onClick: () => handleNavigation(`${project?.editorBasePath}/help/costs-calculation`)
		},
		{
			value: 'project-configuration',
			label: 'Project configuration',
			description: 'Manage project settings, environments, and secrets.',
			keywords: [{ word: 'settings', score: 1 }, { word: 'env', score: 1 }, { word: 'secrets', score: 1 }, { word: 'setup', score: 1 }],
			iconEnd: 'fa-solid fa-cog',
			onClick: () => handleNavigation(`${project?.editorBasePath}/help/project-configuration`)
		},
		{
			value: 'exporting-project',
			label: 'Exporting project',
			description: 'Download your project data and logic.',
			keywords: [{ word: 'download', score: 1 }, { word: 'save', score: 1 }, { word: 'backup', score: 1 }, { word: 'offline', score: 1 }],
			iconEnd: 'fa-solid fa-file-export',
			onClick: () => handleNavigation(`${project?.editorBasePath}/help/exporting-project`)
		},
		{
			value: 'project-publication',
			label: 'Project publication',
			description: 'Deploy your project to a live environment.',
			keywords: [{ word: 'deploy', score: 1 }, { word: 'live', score: 1 }, { word: 'production', score: 1 }, { word: 'release', score: 1 }],
			iconFile: PublishProjectRichIcon,
			iconEnd: 'fa-solid fa-flag-checkered',
			onClick: () => handleNavigation(`${project?.editorBasePath}/help/project-publication`)
		},
		{
			value: 'unpublish-project',
			label: 'Unpublish project',
			description: 'Take your project offline.',
			keywords: [{ word: 'offline', score: 1 }, { word: 'stop', score: 1 }, { word: 'down', score: 1 }, { word: 'revert', score: 1 }],
			iconEnd: 'fa-solid fa-ban',
			onClick: () => handleNavigation(`${project?.editorBasePath}/help/unpublish-project`)
		},
		{
			value: 'cloud-assets',
			label: 'Cloud assets',
			description: 'Manage files and media stored in the cloud.',
			keywords: [{ word: 'files', score: 1 }, { word: 'images', score: 1 }, { word: 'upload', score: 1 }, { word: 'storage', score: 1 }],
			iconFile: CloudAssetsIcon,
			iconEnd: 'fa-solid fa-cloud',
			onClick: () => handleNavigation(`${project?.editorBasePath}/help/cloud-assets`)
		},
		{
			value: 'sql-table',
			label: 'SQL Table',
			description: 'Store structured data permanently across sessions.',
			keywords: [{ word: 'database', score: 1 }, { word: 'db', score: 1 }, { word: 'persist', score: 1 }, { word: 'save', score: 1 }],
			iconFile: DatabaseTableIcon,
			iconEnd: 'fa-solid fa-table',
			onClick: () => handleNavigation(`${project?.editorBasePath}/help/sql-table`)
		},
		{
			value: 'sql-database',
			label: 'SQL Database',
			description: 'Manage a relational database instance.',
			keywords: [{ word: 'database', score: 1 }, { word: 'db', score: 1 }, { word: 'sql', score: 1 }, { word: 'relational', score: 1 }],
			iconFile: RelationalDatabaseIcon,
			iconEnd: 'fa-solid fa-database',
			onClick: () => handleNavigation(`${project?.editorBasePath}/help/sql-database`)
		},
		{
			value: 'http-endpoint-node',
			label: 'HTTP endpoint',
			description: 'Create API endpoints to connect with external services.',
			keywords: [{ word: 'api', score: 1 }, { word: 'rest', score: 1 }, { word: 'webhook', score: 1 }, { word: 'request', score: 1 }],
			iconFile: HTTPEndpointIcon,
			iconEnd: 'fa-solid fa-arrow-right-arrow-left',
			onClick: () => handleNavigation(`${project?.editorBasePath}/help/http-endpoint-node`)
		},
		{
			value: 'container-compute',
			label: 'Container Compute',
			description: 'Run long-running servers and infrastructure logic.',
			keywords: [{ word: 'server', score: 1 }, { word: 'compute', score: 1 }, { word: 'container', score: 1 }, { word: 'docker', score: 1 }],
			iconFile: ContainerComputeIcon,
			iconEnd: 'fa-solid fa-server',
			onClick: () => handleNavigation(`${project?.editorBasePath}/help/container-compute`)
		},
		{
			value: 'shared-drive',
			label: 'Shared Drive',
			description: 'Persistent file storage for your compute nodes.',
			keywords: [{ word: 'drive', score: 1 }, { word: 'volume', score: 1 }, { word: 'storage', score: 1 }, { word: 'disk', score: 1 }],
			iconFile: SharedDriveIcon,
			iconEnd: 'fa-solid fa-hard-drive',
			onClick: () => handleNavigation(`${project?.editorBasePath}/help/shared-drive`)
		},
		{
			value: 'container-terminal',
			label: 'Live Terminal',
			description: 'Access the terminal and logs of your live servers.',
			keywords: [{ word: 'terminal', score: 1 }, { word: 'logs', score: 1 }, { word: 'console', score: 1 }, { word: 'infrastructure', score: 1 }],
			iconFile: TerminalIcon,
			iconEnd: 'fa-solid fa-terminal',
			onClick: () => handleNavigation(`${project?.editorBasePath}/help/container-terminal`)
		},
		{
			value: 'cloud-file-system',
			label: 'Cloud file system',
			description: 'Cloud file storage (AWS S3) buckets for programmatic and UI file management.',
			keywords: [{ word: 'bucket', score: 1 }, { word: 's3', score: 1 }, { word: 'storage', score: 1 }, { word: 'files', score: 1 }, { word: 'cloud', score: 1 }],
			iconFile: CloudFileSystemIcon,
			iconEnd: 'fa-solid fa-cloud',
			onClick: () => handleNavigation(`${project?.editorBasePath}/help/cloud-file-system`)
		}
	];

	return (
		<div
			style={{
				display: 'flex',
				flexDirection: 'column',
				height: '100%',
				gap: '4px'
			}}
		>
			{/* Header */}
			<div
				style={{
					padding: '40px 40px 0 40px',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between'
				}}
			>
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						zIndex: 0,
						gap: '16px'
					}}
				>
					<button className="dialog_back_button" onClick={onClose}>
						<i className="fa-solid fa-arrow-left"></i>
					</button>
					<h1
						style={{
							margin: '0px',
							fontWeight: 500,
							fontSize: '28px',
							marginRight: '24px'
						}}
					>
						Help articles
					</h1>
										<Autocomplete
						id="help-articles-search"
						value={helpOptions.find(o => o.value === (tab || '')) || helpOptions[0]}
						options={helpOptions}
						getOptionLabel={(option) => option.label}
						onChange={(event, newValue) => {
							if (newValue) newValue.onClick();
						}}
						filterOptions={(options, params) => {
							const filter = createFilterOptions<typeof helpOptions[0]>();
							
							if (params.inputValue !== '') {
								const inputValue = params.inputValue.toLowerCase();
								return options.filter((option) => {
									const matchLabel = option.label.toLowerCase().includes(inputValue);
									const matchDesc = option.description.toLowerCase().includes(inputValue);
									const matchKeywords = option.keywords.some(k => k.word.toLowerCase().includes(inputValue));
									return matchLabel || matchDesc || matchKeywords;
								});
							}
							return filter(options, params);
						}}
						renderOption={(props, option) => {
							const { key, ...optionProps } = props as any;
							return (
								<Box component="li" key={key} {...optionProps} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'flex-start', textAlign: 'left', width: '100%', borderBottom: '1px solid #eee', padding: '8px 16px' }}>
									<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '8px', width: '100%' }}>
										{option.iconFile ? (
											<img src={option.iconFile} style={{ width: '20px', height: '20px', objectFit: 'contain' }} alt={option.label} />
										) : option.iconEnd ? (
											<i className={option.iconEnd} style={{ width: '20px', textAlign: 'center' }}></i>
										) : null}
										<strong style={{ textAlign: 'left' }}>{option.label}</strong>
									</div>
									<div style={{ fontSize: '12px', color: 'gray', marginTop: '4px', width: '100%', textAlign: 'left' }}>
										{option.description}
									</div>
								</Box>
							);
						}}
						renderInput={(params) => (
							<TextField
								{...params}
								placeholder="Search help articles..."
								variant="outlined"
								size="small"
								InputProps={{
									...params.InputProps,
									startAdornment: (
										<>
											<i className="fa-solid fa-search" style={{ marginLeft: 8, marginRight: 8, color: 'gray' }} />
											{params.InputProps.startAdornment}
										</>
									)
								}}
								sx={{ width: 300, backgroundColor: 'white', borderRadius: '4px' }}
							/>
						)}
						disableClearable
					/>
				</div>
				<a
					href="https://discord.gg/DR2qZupRQW"
					target="_blank"
					rel="noopener noreferrer"
					style={{
						backgroundColor: '#5865F2',
						color: 'white',
						padding: '8px 16px',
						borderRadius: '6px',
						display: 'flex',
						alignItems: 'center',
						gap: '8px',
						textDecoration: 'none',
						fontWeight: 500,
						fontSize: '14px',
						border: 'none',
						cursor: 'pointer',
						transition: 'background-color 0.2s'
					}}
					onMouseOver={(e) =>
						(e.currentTarget.style.backgroundColor = '#4752C4')
					}
					onMouseOut={(e) =>
						(e.currentTarget.style.backgroundColor = '#5865F2')
					}
				>
					<i
						className="fa-brands fa-discord"
						style={{ fontSize: '18px' }}
					></i>
					Join our Discord
				</a>
			</div>

			{/* Content */}
			{tab ? (
				<div style={{ flex: 1, margin: '30px 82px 0 82px' }}>
					<iframe
						style={{
							border: 'none',
							width: '100%',
							height: '100%'
						}}
						src={`${envConfig.HELP_ARTICLES_URL}/articles/${tab}.html`}
					/>
				</div>
			) : (
				<HelpDialogHomeContent project={project} />
			)}

			{/* Footer */}
			<div
				style={{
					padding: '0 40px 40px 40px',
					alignSelf: 'flex-start',
					display: 'flex',
					flexDirection: 'row',
					justifyContent: 'flex-end',
					width: '100%',
					gap: '16px'
				}}
			>
				<Button
					onClick={() =>
						handleNavigation(`${project?.editorBasePath}`)
					}
				>
					Back
				</Button>
			</div>
		</div>
	);
};

export default HelpDialog;
